import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, FileText, Radar, ShieldAlert, Wallet, ChevronRight, ArrowUpRight } from 'lucide-react';
import { eur } from '../api';
import { Empty } from '../ui';
import { QUICK, bandOf, fmtDay } from './useDashboardData';

function Kpi({ icon: Icon, label, value, testid, hot, sub }) {
  return (
    <div className={`rsd-kpi ${hot ? 'hot' : ''}`}>
      <Icon size={18} className={hot ? 'text-white' : 'text-[#E0463F]'} />
      <small>{label}</small>
      <b data-testid={testid}>{value}</b>
      {sub}
    </div>
  );
}

function ReportRow({ r }) {
  return (
    <Link to={`/portal/report/${r.id}`} className="rsd-row" data-testid={`dashboard-report-${r.id}`}>
      <span className="bar" style={{ background: bandOf(r.rating) }} title={`Rating ${r.rating || 'n.d.'}`} />
      <span className="nm min-w-0"><b>{r.company_name}</b><small>{r.rating ? `Rating ${r.rating} · ` : ''}{r.activity || r.company_id}</small></span>
      <span className="sc">{r.score ?? '–'}<em>/100</em></span>
      <span className="dt">{fmtDay(r.created_at)}</span>
      <ChevronRight size={15} className="ar" />
    </Link>
  );
}

function Ring({ dist }) {
  const total = dist.reduce((s, [, n]) => s + n, 0);
  let acc = 0;
  const used = dist.filter(([, n]) => n > 0);
  const none = dist.filter(([, n]) => n === 0).map(([k]) => k);
  return (
    <div className="rsd-ring" data-testid="dashboard-rating-dist">
      <svg viewBox="0 0 42 42">
        <circle cx="21" cy="21" r="15.9155" fill="none" stroke="#1E1E23" strokeWidth="5" />
        {total > 0 && used.map(([k, n]) => {
          const pct = (n / total) * 100;
          const el = <circle key={k} cx="21" cy="21" r="15.9155" fill="none" stroke={bandOf(k)} strokeWidth="5" strokeDasharray={`${pct} ${100 - pct}`} strokeDashoffset={25 - acc} />;
          acc += pct;
          return el;
        })}
        <text x="21" y="23.5" textAnchor="middle" fill="#EDEDEF" fontSize="8" fontFamily="Caladea">{total}</text>
      </svg>
      <div className="rsd-legend">
        {used.map(([k, n]) => <div key={k}><i style={{ background: bandOf(k) }} />{k} · {n} {n === 1 ? 'azienda' : 'aziende'}</div>)}
        {none.length > 0 && <div className="!text-[#7C7F87]">{none.join(', ')} · nessuna</div>}
      </div>
    </div>
  );
}

export default function DashRosso({ user, data }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const { reports, spent, monitored, checks, dist } = data;
  return (
    <div className="portal-rise">
      <div className="rsd-head" data-testid="dashboard-hero">
        <div>
          <p className="rsd-kicker">Dashboard</p>
          <h1 className="text-3xl lg:text-[36px]">Il tuo <b>cruscotto</b></h1>
        </div>
        <form className="rsd-search" onSubmit={(e) => { e.preventDefault(); navigate(`/portal/ricerca?name=${encodeURIComponent(q)}`); }}>
          <Search size={16} className="text-[#7C7F87] shrink-0" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca azienda per ragione sociale, P.IVA o REA…" data-testid="dashboard-quick-search" />
          <button type="submit" data-testid="dashboard-quick-search-btn">Cerca</button>
        </form>
      </div>
      <div className="rsd-kpis">
        <Kpi hot icon={Wallet} label="Saldo plafond" value={eur(user.wallet_cents)} testid="dashboard-balance"
          sub={<span>Speso finora <strong data-testid="dashboard-spent">{eur(spent)}</strong></span>} />
        <Kpi icon={FileText} label="Report acquistati" value={reports.length} testid="dashboard-reports-count" />
        <Kpi icon={Radar} label="Aziende monitorate" value={monitored} testid="dashboard-monitored-count" />
        <Kpi icon={ShieldAlert} label="Reputational check" value={checks} testid="dashboard-checks-count" />
      </div>
      <div className="grid lg:grid-cols-[1.7fr_1fr] gap-5">
        <div className="rsd-card">
          <div className="flex items-start justify-between">
            <div><p className="rsd-kicker">Archivio</p><h4>Ultimi report acquistati</h4></div>
            <Link to="/portal/report" className="text-xs text-[#E0463F] hover:underline mt-1" data-testid="dashboard-all-reports">Vedi tutti</Link>
          </div>
          {reports.length === 0
            ? <Empty icon={FileText} title="Nessun report ancora" text="Cerca un'azienda e acquista il primo report: verrà salvato qui e potrai riaprirlo senza costi." />
            : reports.slice(0, 6).map((r) => <ReportRow key={r.id} r={r} />)}
        </div>
        <div className="rsd-card">
          <p className="rsd-kicker">Portafoglio</p><h4>Rating delle aziende</h4>
          <Ring dist={dist} />
          <p className="rsd-kicker mt-6 mb-2">Azioni</p>
          {QUICK.map(({ to, icon: Icon, label, id }) => (
            <Link key={id} to={to} className="rsd-act" data-testid={`dashboard-quick-${id}`}>
              <Icon size={17} className="text-[#E0463F]" /><span className="flex-1">{label}</span><ArrowUpRight size={14} className="text-[#5C5F66]" />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
